import React, { useState } from 'react';
import { Spin, Modal, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { STUDY_DATETIME_FORMAT } from '../../../../utils/helper';
import type { OrderDetails } from '../../types';

interface StudyQueueProps {
  orders: OrderDetails[];
  selectedIndex: number;
  loading: boolean;
  onSelect: (order: OrderDetails, index: number) => void;
  /** Remove a study from the loaded list (unload). */
  onUnload: (order: OrderDetails, index: number) => void;
  /**
   * Freeze the queue while the active study is committing (Draft / Reviewed / Sign off)
   * and while its committed report is held on screen. Selecting or unloading during that
   * window races the pending handoff: the click claims a lock and re-steers the viewer,
   * then the handoff fires with its captured index, overrides the selection and tears the
   * freshly-loaded editor down. Cards stay visible (the user can still see what's queued),
   * just non-interactive.
   */
  disabled?: boolean;
}

/**
 * Compact studies queue rendered at the top of the reporting left rail.
 * Each row is a dense two-line entry: patient name + modality/desc + request time.
 * When more than one study is loaded, each card shows a close (X) button that
 * unloads that study after a confirmation.
 */
const StudyQueue: React.FC<StudyQueueProps> = ({ orders, selectedIndex, loading, onSelect, onUnload, disabled = false }) => {
  const [pendingUnload, setPendingUnload] = useState<{ order: OrderDetails; idx: number } | null>(null);
  const { token } = theme.useToken();
  // X is only meaningful when there's more than one loaded study — never let the rail go empty.
  const showClose = orders.length > 1 && !disabled;

  return (
    <Spin spinning={loading}>
      {disabled && orders.length > 0 && (
        <div className="queue-locked-note">Finishing this study…</div>
      )}
      {orders.length === 0 ? (
        <div className="rail-empty">No studies</div>
      ) : (
        orders.map((order, idx) => {
          const po = order.pacs_order;
          return (
            <div className="queue-item-wrap" key={po?.pacs_ord_id ?? idx}>
              <button
                type="button"
                onClick={() => onSelect(order, idx)}
                disabled={disabled}
                className={`queue-item${selectedIndex === idx ? ' active' : ''}${disabled ? ' locked' : ''}`}
              >
                <div className="queue-item-name" title={po?.patient?.pat_name}>
                  {po?.patient?.pat_name}
                </div>
                <div className="queue-item-meta" title={`${po?.po_modality} | ${po?.po_diag_desc}`}>
                  {po?.po_modality && <span className="queue-tag">{po.po_modality}</span>}
                  <span className="queue-item-desc">{po?.po_diag_desc}</span>
                </div>
                <div className="queue-item-date">
                  {moment(po?.po_req_time).format(STUDY_DATETIME_FORMAT)}
                </div>
              </button>
              {showClose && (
                <button
                  type="button"
                  className="queue-item-close"
                  title="Unload study"
                  aria-label="Unload study"
                  onClick={() => setPendingUnload({ order, idx })}
                >
                  <CloseOutlined />
                </button>
              )}
            </div>
          );
        })
      )}

      {pendingUnload && (
        <Modal
          title="Unload Study"
          open
          centered
          width={400}
          className="unload-study-modal"
          okText="Yes"
          cancelText="No"
          okButtonProps={{ type: 'primary' }}
          styles={{
            content: { padding: 0, overflow: 'hidden' },
            header: { background: token.colorPrimary, margin: 0, padding: '0.75rem 1rem' },
            body: { padding: '1.25rem 1rem' },
            footer: { margin: 0, padding: '0 1rem 1rem' },
          }}
          onOk={() => {
            onUnload(pendingUnload.order, pendingUnload.idx);
            setPendingUnload(null);
          }}
          onCancel={() => setPendingUnload(null)}
        >
          Do you want to unload this study [Acc No. {pendingUnload.order?.pacs_order?.po_acc_no}] ?
        </Modal>
      )}
    </Spin>
  );
};

export default StudyQueue;
